import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { Slider } from '@mui/material';
import Cookies from 'js-cookie';

import { LoadingType, PreQualificationStep, setLoadingState, setPreQualificationStep } from 'actions';
import { setApplication } from 'actions/application';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import Input from 'components/Inputs/Input';
import NumberInput from 'components/Inputs/NumberInput';
import Loader from 'components/Loader';
import Select from 'components/Select';
import { NOT_LATIN_ALPHABET_REG_EX } from 'constants/constants';
import { ApplicationType } from 'enums/ApplicationType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { LoanPurpose } from 'enums/LoanPurpose';
import { SwimmingPoolType } from 'enums/SwimmingPoolType';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import { OptionType } from 'utils/inputSelect';
import {
  getErrorMessageForRequiredFields,
  MONETARY_PREFIX,
  removeCurrencySign,
  removeNonDigits,
} from 'utils/stringUtils';
import { isOnlyLatinAlphabet } from 'utils/validationUtils';
import { ButtonType } from 'enums/ButtonType';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { MAX_LOAN_AMOUNT_LENGTH } from 'utils/constants';
import { marketingApi } from 'api/MarketingApi';
import { PrequalificationVersion } from 'enums/ApplicationsVariables';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'routes/routes.utils';

import {
  checkUserShouldBeRedirectToFinMktPortal,
  denormalizeLoanAmountSliderValue,
  normalizeValue,
  roundToStep,
} from './loanInformation.utils';
import {
  LOAN_SLIDER_MARKS,
  MAX_LOAN_FOR_PORTAL,
  MAXIMUM_STEPS,
  MIN_LOAN_AMOUNT,
  LOAN_TYPE_CAPTION,
  OTHER_HOME_IMPROVEMENT_CAPTION,
} from './loanInformation.constants';
import styles from './LoanInformation.module.scss';

const ApplicationTypeOptions: OptionType[] = [
  {
    label: 'Individual Application',
    value: ApplicationType.Individual,
  },
  {
    label: 'Joint Application',
    value: ApplicationType.Joint,
  },
];

const getSelectOptions = (selectOptionsType: { [key: string]: string }): OptionType[] => {
  const selectOptions = [];

  for (let optionKey in selectOptionsType) {
    selectOptions.push({
      label: selectOptionsType[optionKey],
      value: selectOptionsType[optionKey],
    });
  }

  return selectOptions;
};

const LoanInformation = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const application = useAppSelector((state) => state.application);
  const borrower = useAppSelector((state) => state.borrower);
  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const {
    register,
    unregister,
    formState: { errors },
    setValue,
    trigger,
    watch,
    control,
    clearErrors,
    handleSubmit,
  } = useForm({
    defaultValues: {
      [LoanInformationVariables.LoanType]: application[LoanInformationVariables.LoanType],
      [LoanInformationVariables.LoanPurpose]: application[LoanInformationVariables.LoanPurpose],
      [LoanInformationVariables.SwimmingPoolType]: application[LoanInformationVariables.SwimmingPoolType],
      [LoanInformationVariables.LoanAmount]: +(application[LoanInformationVariables.LoanAmount] || 0),
      [LoanInformationVariables.OtherHomeImprovementType]:
        application[LoanInformationVariables.OtherHomeImprovementType],
    },
  });

  const watcher = watch();
  const [sliderValue, setSliderValue] = useState(normalizeValue(watcher[LoanInformationVariables.LoanAmount]));

  useEffect(() => {
    register(LoanInformationVariables.LoanType, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanType),
    });
    register(LoanInformationVariables.LoanPurpose, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanPurpose),
    });
    register(LoanInformationVariables.LoanAmount, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.LoanAmount),
      validate: (value) => {
        const loanAmount = +removeNonDigits(value);

        if (loanAmount > MAX_LOAN_FOR_PORTAL) {
          return 'Loan Amount must be less or equal to $500,000';
        }

        if (loanAmount < MIN_LOAN_AMOUNT) {
          return 'Loan Amount must be greater or equal to $1,000';
        }
      },
    });
  }, [register, unregister, watcher]);

  useEffect(() => {
    if (watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.OtherHomeImprovement) {
      register(LoanInformationVariables.OtherHomeImprovementType, {
        required: getErrorMessageForRequiredFields(LoanInformationVariables.OtherHomeImprovementType),
        validate: (value) =>
          isOnlyLatinAlphabet(value) || 'Please enter a valid Type. Type can only contain Latin letters',
      });
    }

    if (watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.SwimmingPool) {
      register(LoanInformationVariables.SwimmingPoolType, {
        required: getErrorMessageForRequiredFields(LoanInformationVariables.SwimmingPoolType),
      });
    }

    return () => {
      unregister(LoanInformationVariables.OtherHomeImprovementType);
      unregister(LoanInformationVariables.SwimmingPoolType);
    };
  }, [register, unregister, watcher[LoanInformationVariables.LoanPurpose]]);

  useEffect(() => {
    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.LoanInformation,
          prequalificationVersion: PrequalificationVersion.First,
          borrowerId: borrower.borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const validateLoanForm = handleSubmit(async () => {
    try {
      const result = await trigger();

      if (result) {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

        const loanAmount = +removeNonDigits(watcher[LoanInformationVariables.LoanAmount]);

        if (checkUserShouldBeRedirectToFinMktPortal(loanAmount)) {
          window.location.href = process.env.REACT_APP_LOW_LOAN_AMOUNT_REDIRECT_URL!;

          return;
        }

        dispatch(
          setApplication({
            [LoanInformationVariables.LoanType]: watcher[LoanInformationVariables.LoanType] as ApplicationType,
            [LoanInformationVariables.LoanPurpose]: watcher[LoanInformationVariables.LoanPurpose] as LoanPurpose,
            [LoanInformationVariables.OtherHomeImprovementType]:
              watcher[LoanInformationVariables.OtherHomeImprovementType],
            [LoanInformationVariables.SwimmingPoolType]: watcher[
              LoanInformationVariables.SwimmingPoolType
            ] as SwimmingPoolType,
            [LoanInformationVariables.LoanAmount]: +removeNonDigits(watcher[LoanInformationVariables.LoanAmount]),
          }),
        );
        dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
    }
  });

  const handleBack = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIdentity));
  };

  const handleLoanAmountInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = +removeNonDigits(removeCurrencySign(event.target.value));

    clearErrors(LoanInformationVariables.LoanAmount);
    setValue(LoanInformationVariables.LoanAmount, newValue);

    setSliderValue(normalizeValue(newValue));

    trigger();
  };

  const handleSliderChange = (event: any, newValue: number | number[]) => {
    const denormalizedValue = denormalizeLoanAmountSliderValue(newValue as number);
    const roundedValue = roundToStep(denormalizedValue);

    setSliderValue(normalizeValue(roundedValue));
    setValue(LoanInformationVariables.LoanAmount, roundedValue);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className={styles.infoContainer}>
            <div className={styles.infoContent}>Inquiry Does NOT Affect Your Credit Scores</div>
          </div>
          <FormHeader title="Loan Information" percent={11} />
          <FormBody formBodyClassName={styles.formBody}>
            <div className={styles.loanAmountContainer}>
              <NumberInput
                label={'How much do you want to borrow?'}
                prefix={MONETARY_PREFIX}
                placeholder={MONETARY_PREFIX}
                thousandSeparator
                inputContainerClassName={styles.loanAmountInputContainer}
                inputClassName={styles.loanAmountInput}
                labelClassName={styles.loanAmountLabel}
                name={LoanInformationVariables.LoanAmount}
                onChange={handleLoanAmountInputChange}
                errorMessage={errors[LoanInformationVariables.LoanAmount]?.message}
                value={watcher[LoanInformationVariables.LoanAmount]}
                maxLength={MAX_LOAN_AMOUNT_LENGTH}
                allowNegative={false}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
              <Slider
                aria-label="Loan Amount"
                value={sliderValue}
                step={1}
                marks={LOAN_SLIDER_MARKS}
                min={0}
                max={MAXIMUM_STEPS}
                classes={{
                  root: styles.loanAmountSlider,
                  mark: styles.loanAmountSliderMark,
                  markLabel: styles.loanAmountSliderMarkLabel,
                  rail: styles.loanAmountSliderRail,
                  thumb: styles.loanAmountSliderThumb,
                }}
                valueLabelDisplay="auto"
                onChange={handleSliderChange}
                valueLabelFormat={(value) =>
                  `$${Math.round(denormalizeLoanAmountSliderValue(value)).toLocaleString('en-US')}`
                }
              />
            </div>
            <div className={styles.rowContainer}>
              <Select
                useFormControl
                required
                control={control}
                onChange={(event: any) => {
                  setValue(LoanInformationVariables.LoanPurpose, event.value as LoanPurpose);
                  trigger(LoanInformationVariables.LoanPurpose);
                }}
                name={LoanInformationVariables.LoanPurpose}
                label={LoanInformationVariables.LoanPurpose}
                options={getSelectOptions(LoanPurpose)}
                value={getSelectOptions(LoanPurpose).find(
                  (option: OptionType) => watcher[LoanInformationVariables.LoanPurpose!] === option.value,
                )}
                placeholder="Select Your Loan Purpose"
                inputContainerClassName={styles.formInput}
                errorMessage={errors[LoanInformationVariables.LoanPurpose]?.message}
              />
            </div>
            {watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.SwimmingPool && (
              <Select
                useFormControl
                required
                control={control}
                onChange={(event: any) => {
                  setValue(LoanInformationVariables.SwimmingPoolType, event.value as SwimmingPoolType);
                  trigger(LoanInformationVariables.SwimmingPoolType);
                }}
                name={LoanInformationVariables.SwimmingPoolType}
                label={LoanInformationVariables.SwimmingPoolType}
                options={getSelectOptions(SwimmingPoolType)}
                value={getSelectOptions(SwimmingPoolType).find(
                  (option: OptionType) => watcher[LoanInformationVariables.SwimmingPoolType!] === option.value,
                )}
                caption="Please Specify Swimming Pool Type"
                inputContainerClassName={styles.formInput}
                errorMessage={errors[LoanInformationVariables.SwimmingPoolType]?.message}
              />
            )}
            {watcher[LoanInformationVariables.LoanPurpose] === LoanPurpose.OtherHomeImprovement && (
              <Input
                className={styles.formInput}
                errorMessage={errors[LoanInformationVariables.OtherHomeImprovementType]?.message}
                name={LoanInformationVariables.OtherHomeImprovementType}
                label={LoanInformationVariables.OtherHomeImprovementType}
                onChange={(event) => {
                  let filteredLatinAlphabet = event.target.value.replace(NOT_LATIN_ALPHABET_REG_EX, '');

                  setValue(LoanInformationVariables.OtherHomeImprovementType, filteredLatinAlphabet);
                  trigger(LoanInformationVariables.OtherHomeImprovementType);
                }}
                value={watcher[LoanInformationVariables.OtherHomeImprovementType]}
                caption={OTHER_HOME_IMPROVEMENT_CAPTION}
              />
            )}
            <Select
              useFormControl
              required
              control={control}
              onChange={(event: any) => {
                setValue(LoanInformationVariables.LoanType, event.value as ApplicationType);
                trigger(LoanInformationVariables.LoanType);
              }}
              name={LoanInformationVariables.LoanType}
              label={LoanInformationVariables.LoanType}
              options={ApplicationTypeOptions}
              value={ApplicationTypeOptions.find(
                (option) => watcher[LoanInformationVariables.LoanType!] === option.value,
              )}
              placeholder="Select Your Loan Type"
              inputContainerClassName={styles.formInput}
              errorMessage={errors[LoanInformationVariables.LoanType]?.message}
              caption={LOAN_TYPE_CAPTION}
            />
          </FormBody>
          <div className={styles.navigationButtons}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={handleBack}>
              Back
            </Button>
            <Button onClick={validateLoanForm} withArrow>
              Next
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default LoanInformation;
